/**
 * src/lib/AppDataContext.jsx — Shared data context (raw snake_case shape)
 * AC Nautitech.
 *
 * Wraps useAppData and distributes via React Context.
 * Returns raw Supabase rows. See docs/SHAPE_CONTRACT.md.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAppData } from './useAppData';
import DelayReasonModal from '../components/shared/DelayReasonModal';
import { normalizeTaskStatus } from './constants/taskStatus';
import AuthContext from '../auth/AuthContext';
import { isOfflineTasksEnabled } from './featureFlags';

// REMOVED 2026-04-07 PM: in-memory synthetic data prepend.
//
// The previous behavior prepended SYNTHETIC_DATA fixtures to the displayed
// list, but every save/move/create/drag against those rows failed silently
// because the API has no record of them. Inline edits reverted, drag-and-drop
// didn't persist, task creation 406'd, etc.
//
// Replaced with Settings → "Seed test data" button that calls
// /api/data?action=synthetic.seed and inserts REAL [TEST]-prefixed rows into
// Supabase. Use synthetic.purge to clean up. See lib/handlers/synthetic-data.js.

// Exported so callers can opt into a NULL-SAFE read via React.useContext
// directly (returning null when the provider is absent — useful for
// isolation tests). Use useAppDataContext() when you require the provider.
export const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  // Null-safe read of the auth context (useContext, NOT useAuth — must not throw
  // in isolation tests that render without <AuthProvider>). Drives the offline
  // feature flag.
  //
  // Fall back to the auth `user` when `profile` is null: on an OFFLINE cold-boot
  // (installed PWA reopened on a plane) AuthContext can only restore the
  // persisted session `user` — the profile fetch needs the network. The session
  // user still carries `.email`, and isOfflineTasksEnabled() accepts either a
  // profile (role) OR the auth user (email) to identify the CEO. Without this
  // fallback the flag read false offline → every domain skipped its cache → the
  // Focus/Projects/Pipeline boards came up EMPTY in flight even though the data
  // was cached. (CEO report 2026-06-11.)
  const authCtx = useContext(AuthContext);
  const offlineEnabled = isOfflineTasksEnabled(authCtx?.profile ?? authCtx?.user ?? null);

  const data = useAppData({ offlineEnabled });
  const mergedData = data;

  // ─── Delay intercept state ─────────────────────────
  const [pendingBlock, setPendingBlock] = useState(null);

  const interceptedUpdateStatus = useCallback(async (taskId, newStatus) => {
    if (normalizeTaskStatus(newStatus) === 'blocked') {
      const task = mergedData.tasks.find(t => t.id === taskId);
      setPendingBlock({ taskId, task });
      return;
    }
    return mergedData.handleUpdateStatus(taskId, newStatus);
  }, [mergedData.handleUpdateStatus, mergedData.tasks]);

  const handleBlockConfirm = useCallback(async (reason) => {
    if (!pendingBlock) return;
    const { taskId, task } = pendingBlock;
    const existingNotes = task?.notes || '';
    const prefix = `[BLOCKED ${new Date().toISOString().slice(0, 10)}] ${reason}`;
    const updatedNotes = existingNotes ? `${prefix}\n\n${existingNotes}` : prefix;

    await mergedData.handleUpdateTask(taskId, { status: 'blocked', notes: updatedNotes });
    setPendingBlock(null);
  }, [pendingBlock, mergedData.handleUpdateTask]);

  const handleBlockCancel = useCallback(() => {
    setPendingBlock(null);
  }, []);

  // ─── Filter persistence per view ──────────────────
  const [viewFilters, setViewFiltersState] = useState({
    gantt: {}, focus: {}, projects: {}, team: {},
    worklogs: {}, directory: {}, crm: {}, invoiceQueue: {}, reviewQueue: {},
  });

  const getViewFilters = useCallback((viewId) => viewFilters[viewId] || {}, [viewFilters]);

  const setViewFilter = useCallback((viewId, key, value) => {
    setViewFiltersState(prev => ({
      ...prev,
      [viewId]: { ...(prev[viewId] || {}), [key]: value },
    }));
  }, []);

  const clearViewFilters = useCallback((viewId) => {
    setViewFiltersState(prev => ({ ...prev, [viewId]: {} }));
  }, []);

  // TICKET-048 (AEGIS HIGH 2026-05-01) — memoize ctxValue. Without useMemo,
  // every parent re-render produced a fresh object identity, forcing every
  // useAppDataContext consumer to re-run effects/memos that depend on the
  // context value. Stability is now contingent on mergedData identity from
  // useAppData() being stable across renders too — that's a separate
  // upstream concern; this useMemo is the necessary-but-not-sufficient
  // structural fix.
  const ctxValue = useMemo(
    () => ({
      ...mergedData,
      handleUpdateStatus: interceptedUpdateStatus,
      viewFilters,
      getViewFilters,
      setViewFilter,
      clearViewFilters,
    }),
    [mergedData, interceptedUpdateStatus, viewFilters, getViewFilters, setViewFilter, clearViewFilters],
  );

  return (
    <AppDataContext.Provider value={ctxValue}>
      {children}
      {pendingBlock && (
        <DelayReasonModal
          task={pendingBlock.task}
          onConfirm={handleBlockConfirm}
          onCancel={handleBlockCancel}
        />
      )}
    </AppDataContext.Provider>
  );
}

export function useAppDataContext() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppDataContext must be used inside AppDataProvider');
  return ctx;
}
